import type { WorkflowDefinition, WorkflowNode } from '@corner/shared';
import type { WorkflowRunSnapshot, WorkflowRunStep } from '../hooks/useWorkflowRunStream';
import type { WorkflowThreadSummary } from '../types';

export type { WorkflowThreadSummary };

function orderStepsForSummary(steps: WorkflowRunStep[], definition: WorkflowDefinition): WorkflowRunStep[] {
  const order = new Map<string, number>();
  definition.graph.nodes.forEach((n: WorkflowNode, i: number) => order.set(n.nodeId, i));
  return [...steps].sort((a, b) => (order.get(a.nodeId) ?? 999) - (order.get(b.nodeId) ?? 999));
}

const NODE_SHORT: Record<string, string> = {
  document_input: 'Read document',
  review_task: 'Review',
  extraction_task: 'Extract',
  classify_task: 'Classify',
  drafting_task: 'Draft',
  export_action: 'Export',
  response: 'Output',
  dispatch_for_signature: 'Send for signature',
};

function collectFileHint(vars: Record<string, unknown>): { fileId?: string; fileName?: string } {
  let best: { fileId?: string; fileName?: string } = {};

  const visit = (obj: unknown) => {
    if (obj == null) return;
    if (typeof obj !== 'object') return;
    if (Array.isArray(obj)) {
      obj.forEach(visit);
      return;
    }
    const r = obj as Record<string, unknown>;
    if (typeof r.fileId === 'string' && r.fileId.length >= 8) {
      best = {
        fileId: r.fileId,
        fileName: typeof r.fileName === 'string' ? r.fileName : best.fileName,
      };
    }
    if (typeof r.documentId === 'string' && r.documentId.length >= 8) {
      best.fileId = best.fileId ?? r.documentId;
    }
    for (const v of Object.values(r)) visit(v);
  };

  visit(vars);
  return best;
}

/** Compact summary for chat thread (competitor-style “read → workflow → file” affordances). */
export function buildWorkflowThreadSummary(run: WorkflowRunSnapshot): WorkflowThreadSummary {
  const def = run.definition;
  const ordered = orderStepsForSummary(run.steps ?? [], def);
  const nodesById = new Map<string, WorkflowNode>(
    def.graph.nodes.map((n: WorkflowNode) => [n.nodeId, n]),
  );

  const steps = ordered.map((s) => {
    const node = nodesById.get(s.nodeId);
    const typeKey = node?.type ?? '';
    const label =
      node?.type === 'document_input'
        ? 'Read document'
        : (node?.label?.trim() || NODE_SHORT[typeKey] || 'Step');
    const out = s.output;
    const detail =
      typeof out === 'string' && out.length > 0
        ? `${out.slice(0, 140)}${out.length > 140 ? '…' : ''}`
        : undefined;
    return {
      id: s.nodeId,
      label,
      detail,
      done: s.status === 'completed' || s.status === 'skipped',
    };
  });

  const wfLabel = def.label || 'Workflow';
  const hint = collectFileHint(run.collectedVariables ?? {});
  const primaryDownloadFileId = hint.fileId;
  const primaryDownloadFileName = hint.fileName;

  const ranLine = `Ran workflow: **${wfLabel}**`;
  const summaryText = primaryDownloadFileName
    ? `${ranLine} · produced **${primaryDownloadFileName}**`
    : ranLine;

  return {
    workflowLabel: wfLabel,
    runId: run.id,
    steps,
    primaryDownloadFileId,
    primaryDownloadFileName,
    summaryText,
  };
}
